/**
 * Settings → Linked Identities.
 *
 * Lists every linked OAuth identity for the current user with provider,
 * email, linked_at, last_used_at. Each row gets:
 *   - "Unlink" button (disabled if remaining auth methods would drop below 1)
 *   - "Link" button for any provider that's enabled and not already linked
 *
 * Last-method protection is enforced server-side (CLAUSE constraint #10);
 * the UI disables the button preemptively but the Worker is the authority.
 */

import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";
import type { SsoProvider, SsoProviderInfo } from "@/components/SsoButtons";

interface IdentityRow {
  id: string;
  provider: SsoProvider;
  email: string | null;
  email_verified: boolean;
  display_name: string | null;
  linked_at: string;
  last_used_at: string | null;
}

interface IdentitiesResponse {
  identities: IdentityRow[];
  password_set: boolean;
}

interface ProvidersResponse {
  sso_enabled: boolean;
  providers: SsoProviderInfo[];
}

const LABELS: Record<SsoProvider, string> = {
  microsoft: "Microsoft 365",
  google: "Google",
  cloudflare: "Cloudflare Access",
  apple: "Apple",
};

export function SettingsIdentitiesPage(): JSX.Element {
  const [identities, setIdentities] = useState<IdentityRow[]>([]);
  const [passwordSet, setPasswordSet] = useState<boolean>(false);
  const [providers, setProviders] = useState<SsoProviderInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [actioningId, setActioningId] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    try {
      const [data, provData] = await Promise.all([
        authFetch<IdentitiesResponse>("/api/auth/sso/identities"),
        fetch("/api/auth/sso/providers")
          .then((r) => r.json() as Promise<{ data: ProvidersResponse }>)
          .then((j) => j.data),
      ]);
      setIdentities(data.identities);
      setPasswordSet(data.password_set);
      setProviders(provData.providers);
    } catch (err) {
      toast.error((err as Error).message ?? "Failed to load identities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // Surface the post-link toast.
    if (new URLSearchParams(window.location.search).get("linked") === "1") {
      toast.success("Identity linked");
    }
  }, []);

  const totalMethods = identities.length + (passwordSet ? 1 : 0);

  const handleUnlink = async (id: string): Promise<void> => {
    setActioningId(id);
    try {
      await authFetch(`/api/auth/sso/identities/${id}`, { method: "DELETE" });
      toast.success("Identity removed");
      await refresh();
    } catch (err) {
      toast.error((err as Error).message ?? "Unlink failed");
    } finally {
      setActioningId(null);
    }
  };

  const linkUrl = (provider: SsoProvider): string => {
    const params = new URLSearchParams({ intent: "link", return_to: "/settings/identities" });
    return `/api/auth/sso/${provider}/start?${params.toString()}`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="font-display text-3xl text-charcoal dark:text-charcoal-dark">Linked Identities</h1>
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  const linkedSet = new Set(identities.map((i) => i.provider));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-charcoal dark:text-charcoal-dark">
          Linked Identities
        </h1>
        <p className="mt-1 text-muted-foreground">
          Sign-in methods on your account. You always need at least one.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your sign-in methods</CardTitle>
          <CardDescription>
            {totalMethods === 1
              ? "You have one method. Link another before removing this one."
              : `You have ${totalMethods} sign-in methods.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between rounded-md border border-border bg-surface px-4 py-3 dark:bg-surface-raised">
            <div>
              <p className="font-medium text-charcoal dark:text-charcoal-dark">Password</p>
              <p className="text-sm text-muted-foreground">
                {passwordSet ? "Email + password is enabled." : "No password set (SSO-only)."}
              </p>
            </div>
            <span className="font-mono text-2xs uppercase tracking-wider text-muted-foreground">
              {passwordSet ? "Active" : "Disabled"}
            </span>
          </div>
          {identities.map((i) => {
            const wouldStrand = totalMethods <= 1;
            return (
              <div
                key={i.id}
                className="flex items-center justify-between rounded-md border border-border bg-surface px-4 py-3 dark:bg-surface-raised"
              >
                <div>
                  <p className="font-medium text-charcoal dark:text-charcoal-dark">
                    {LABELS[i.provider]}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {i.email ?? i.display_name ?? i.provider}
                    {i.last_used_at ? ` • last used ${new Date(i.last_used_at).toLocaleDateString()}` : null}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={wouldStrand || actioningId === i.id}
                  onClick={() => void handleUnlink(i.id)}
                  title={
                    wouldStrand
                      ? "Add another sign-in method first."
                      : "Remove this identity."
                  }
                >
                  {actioningId === i.id ? "Removing…" : "Unlink"}
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add a sign-in method</CardTitle>
          <CardDescription>Pick a provider to link to this account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {providers
            .filter((p) => p.enabled && p.configured && !linkedSet.has(p.provider))
            .map((p) => (
              <a
                key={p.provider}
                href={linkUrl(p.provider)}
                className="flex items-center justify-between rounded-md border border-border bg-surface px-4 py-3 text-charcoal hover:bg-accent dark:bg-surface-raised dark:text-charcoal-dark dark:hover:bg-accent/40"
              >
                <span className="font-medium">{LABELS[p.provider]}</span>
                <span className="font-mono text-2xs uppercase tracking-wider text-burnt-orange dark:text-burnt-orange-dark">
                  Link →
                </span>
              </a>
            ))}
          {providers.filter((p) => p.enabled && p.configured && !linkedSet.has(p.provider)).length === 0 && (
            <p className="text-sm text-muted-foreground">All available providers are already linked.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
